import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FiShoppingCart, FiTrash2, FiMinus, FiPlus,
  FiArrowRight, FiPackage, FiTag, FiChevronLeft,
  FiCheck, FiAlertCircle, FiUser, FiUserPlus,
  FiLogIn, FiEye, FiEyeOff, FiMapPin,
  FiInfo, FiCreditCard, FiTruck, FiCopy, FiExternalLink
} from "react-icons/fi";
import { useCart, useCustomer } from "../context/ShopContext";
import "./Cart.css";

/* ── Image resolver ── */
function shopImg(filename) {
  if (!filename) return null;
  return `${process.env.REACT_APP_API_URL}/uploads/shop/${filename}`;
}

const fmt = (n) =>
  "R " + Number(n).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const WHATSAPP_NUMBER = "27829362678";
const ORDERS_EMAIL    = "orders@noelautomotiverepaires.com";

/* ══════════════════════════════════════════════════════════════
   COUPON DISCOUNT CALCULATOR
   ──────────────────────────────────────────────────────────────
   Rules:
   • coupon.category_ids = [] (empty)  → applies to ALL cart items
   • coupon.category_ids = [1,3]       → applies ONLY to items whose
                                         category_id is in that list
   • min_order_value is checked against the FULL cart subtotal
   • For percent coupons: discount = eligibleSubtotal × rate
   • For fixed coupons:   discount = min(fixedValue, eligibleSubtotal)
   Returns { discountAmount, eligibleSubtotal, eligibleItems }
   ══════════════════════════════════════════════════════════════ */
function calcCouponDiscount(cartItems, coupon, fullCartTotal) {
  if (!coupon) return { discountAmount: 0, eligibleSubtotal: 0, eligibleItems: [] };

  const restrictedCatIds = Array.isArray(coupon.category_ids)
    ? coupon.category_ids.map(Number).filter(Boolean)
    : [];

  // Which items does this coupon cover?
  const eligibleItems =
    restrictedCatIds.length === 0
      ? cartItems                                                   // all products
      : cartItems.filter(item =>
          restrictedCatIds.includes(Number(item.category_id))
        );

  const eligibleSubtotal = eligibleItems.reduce(
    (sum, item) => sum + Number(item.sale_price || item.price) * item.qty,
    0
  );

  // min_order_value guard — checked against the full cart total
  if (
    coupon.min_order_value &&
    Number(coupon.min_order_value) > 0 &&
    fullCartTotal < Number(coupon.min_order_value)
  ) {
    return { discountAmount: 0, eligibleSubtotal, eligibleItems };
  }

  let discountAmount = 0;
  if (coupon.discount_type === "percent") {
    discountAmount = (eligibleSubtotal * Number(coupon.discount_value)) / 100;
  } else {
    // Fixed — cannot exceed eligible subtotal
    discountAmount = Math.min(Number(coupon.discount_value), eligibleSubtotal);
  }

  return {
    discountAmount:  Math.round(discountAmount  * 100) / 100,
    eligibleSubtotal: Math.round(eligibleSubtotal * 100) / 100,
    eligibleItems,
  };
}

/* ══════════════════════════════════════
   BANKING DETAILS + INSTRUCTIONS PANEL
   ══════════════════════════════════════ */
function BankingPanel({ paymentMethod, bankingDetails }) {
  const [copied, setCopied] = useState(null);

  const copy = (text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  const DeliveryTable = () => (
    <div className="cart-delivery-table">
      <div className="cart-delivery-table__head">
        <FiTruck size={13} /> Delivery &amp; Collection
      </div>
      <table>
        <tbody>
          <tr>
            <td><FiShoppingCart size={13} /> Collection in store</td>
            <td className="cart-delivery-table__free">FREE</td>
          </tr>
          <tr>
            <td><FiTruck size={13} /> Nationwide delivery</td>
            <td>R 250.00</td>
          </tr>
        </tbody>
      </table>
    </div>
  );

  if (paymentMethod === "Cash") {
    return (
      <div className="cart-side-panel">
        <div className="cart-side-panel__header cart-side-panel__header--cash">
          <FiShoppingCart size={16} /><span>Cash on Collection</span>
        </div>
        <div className="cart-side-panel__body">
          <p className="cart-side-panel__note">
            Payment is due in cash when you collect your order from our premises.
            We will contact you as soon as your order is ready for collection.
          </p>
          <DeliveryTable />
        </div>
      </div>
    );
  }

  return (
    <div className="cart-side-panel">
      <div className="cart-side-panel__header">
        <FiCreditCard size={16} /><span>EFT Banking Details</span>
      </div>

      <div className="cart-side-panel__body">
        {!bankingDetails && (
          <p className="cart-side-panel__loading">Loading banking details…</p>
        )}
        {bankingDetails && bankingDetails.length === 0 && (
          <p className="cart-side-panel__loading">No banking details available.</p>
        )}
        {bankingDetails && bankingDetails.map((b, idx) => (
          <div key={b.id} className="cart-bank-card">
            <div className="cart-bank-card__name">{b.bank_name}</div>
            <table className="cart-bank-table">
              <tbody>
                <tr>
                  <th>Account Name</th>
                  <td>
                    {b.account_name}
                    <button className="cart-copy-btn" onClick={() => copy(b.account_name, `an${idx}`)} title="Copy">
                      {copied === `an${idx}` ? <FiCheck size={11} /> : <FiCopy size={11} />}
                    </button>
                  </td>
                </tr>
                <tr>
                  <th>Account No.</th>
                  <td>
                    {b.account_number}
                    <button className="cart-copy-btn" onClick={() => copy(b.account_number, `num${idx}`)} title="Copy">
                      {copied === `num${idx}` ? <FiCheck size={11} /> : <FiCopy size={11} />}
                    </button>
                  </td>
                </tr>
                {b.branch_code && (
                  <tr>
                    <th>Branch Code</th>
                    <td>
                      {b.branch_code}
                      <button className="cart-copy-btn" onClick={() => copy(b.branch_code, `br${idx}`)} title="Copy">
                        {copied === `br${idx}` ? <FiCheck size={11} /> : <FiCopy size={11} />}
                      </button>
                    </td>
                  </tr>
                )}
                {b.account_type && (
                  <tr><th>Account Type</th><td>{b.account_type}</td></tr>
                )}
                <tr>
                  <th>Payment Type</th>
                  <td><span className="cart-bank-table__badge">Instant EFT Only</span></td>
                </tr>
                <tr>
                  <th>Reference</th>
                  <td><span className="cart-bank-table__ref">Your Order Number</span></td>
                </tr>
              </tbody>
            </table>
          </div>
        ))}

        <DeliveryTable />

        <div className="cart-pop">
          <div className="cart-pop__head">
            <FiInfo size={13} /> Proof of Payment Instructions
          </div>
          <table className="cart-pop__table">
            <tbody>
              <tr>
                <th>Step 1</th>
                <td>Complete your payment via <strong>Instant EFT</strong> using the banking details above. Use your <strong>order number</strong> as the payment reference.</td>
              </tr>
              <tr>
                <th>Step 2</th>
                <td>Send your proof of payment via <strong>WhatsApp</strong> or <strong>Email</strong>:</td>
              </tr>
              <tr>
                <th>WhatsApp</th>
                <td>
                  <a
                    href={`https://wa.me/${WHATSAPP_NUMBER}?text=Hi%2C%20please%20find%20my%20proof%20of%20payment%20attached.`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cart-pop__link cart-pop__link--whatsapp"
                  >
                    <WhatsAppIcon />
                    +27 82 936 2678
                    <FiExternalLink size={11} />
                  </a>
                </td>
              </tr>
              <tr>
                <th>Email</th>
                <td>
                  <a
                    href={`mailto:${ORDERS_EMAIL}?subject=Proof%20of%20Payment%20-%20Order`}
                    className="cart-pop__link cart-pop__link--email"
                  >
                    {ORDERS_EMAIL}
                  </a>
                </td>
              </tr>
              <tr>
                <th>Step 3</th>
                <td>Your order will be <strong>processed and confirmed</strong> once payment has been verified.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function WhatsAppIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" width="13" height="13">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/>
    </svg>
  );
}

/* ══════════════════════════════════════
   GOOGLE PLACES ADDRESS AUTOCOMPLETE
   ══════════════════════════════════════ */
function AddressAutocomplete({ value, onChange, name }) {
  const inputRef = useRef(null);
  const autoRef  = useRef(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (window.google?.maps?.places) { setLoaded(true); return; }
    if (document.getElementById("gmap-script")) {
      const check = setInterval(() => {
        if (window.google?.maps?.places) { setLoaded(true); clearInterval(check); }
      }, 200);
      return () => clearInterval(check);
    }
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_KEY || "";
    if (!apiKey) return;
    const script = document.createElement("script");
    script.id    = "gmap-script";
    script.src   = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.onload = () => setLoaded(true);
    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    if (!loaded || !inputRef.current || autoRef.current) return;
    try {
      const ac = new window.google.maps.places.Autocomplete(inputRef.current, {
        componentRestrictions: { country: "za" },
        fields: ["formatted_address"],
        types: ["address"],
      });
      ac.addListener("place_changed", () => {
        const place = ac.getPlace();
        if (place?.formatted_address) {
          onChange({ target: { name, value: place.formatted_address } });
        }
      });
      autoRef.current = ac;
    } catch (e) {
      console.warn("Google Places init failed:", e);
    }
  }, [loaded, name, onChange]);

  return (
    <div className="cart-address-wrap">
      <FiMapPin size={14} className="cart-address-wrap__icon" />
      <textarea
        ref={inputRef}
        name={name}
        value={value}
        onChange={onChange}
        rows={2}
        placeholder="Start typing your address…"
        className="cart-address-wrap__input"
        autoComplete="off"
      />
      {!process.env.REACT_APP_GOOGLE_MAPS_KEY && (
        <span className="cart-address-wrap__hint">
          Tip: Add <code>REACT_APP_GOOGLE_MAPS_KEY</code> to <code>.env</code> to enable address suggestions
        </span>
      )}
    </div>
  );
}

/* ══════════════════════════════════════
   AUTH GATE
   ══════════════════════════════════════ */
function AuthGate({ onContinue }) {
  const { login, customer } = useCustomer();
  const [mode,   setMode]   = useState("choose");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error,  setError]  = useState("");
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [regForm,   setRegForm]   = useState({
    first_name: "", last_name: "", email: "", phone: "",
    password: "", password2: "",
    address: "", city: "", province: "", postal_code: "",
  });

  useEffect(() => { if (customer) onContinue("details", customer); }, [customer]);

  const handleLogin = async (e) => {
    e.preventDefault(); setLoading(true); setError("");
    try {
      const r    = await fetch(`${process.env.REACT_APP_API_URL}/api/customers/login`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(loginForm) });
      const data = await r.json();
      if (!r.ok) throw new Error(data.message || "Login failed.");
      login(data.customer, data.token);
      onContinue("details", data.customer);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (regForm.password !== regForm.password2) { setError("Passwords do not match."); return; }
    setLoading(true); setError("");
    try {
      const r    = await fetch(`${process.env.REACT_APP_API_URL}/api/customers/register`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(regForm) });
      const data = await r.json();
      if (!r.ok) throw new Error(data.message || "Registration failed.");
      login(data.customer, data.token);
      onContinue("details", data.customer);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  if (mode === "choose") return (
    <div className="cart-auth">
      <div className="cart-auth__header">
        <h2>How would you like to continue?</h2>
        <p>Sign in to track your order or continue as a guest.</p>
      </div>
      <div className="cart-auth__options">
        <button className="cart-auth__opt" onClick={() => setMode("login")}>
          <div className="cart-auth__opt-icon"><FiLogIn size={26} /></div>
          <div className="cart-auth__opt-text"><strong>Sign In</strong><span>Use your existing account</span></div>
          <FiArrowRight size={16} className="cart-auth__opt-arrow" />
        </button>
        <button className="cart-auth__opt" onClick={() => setMode("register")}>
          <div className="cart-auth__opt-icon"><FiUserPlus size={26} /></div>
          <div className="cart-auth__opt-text"><strong>Create Account</strong><span>Register for faster checkout</span></div>
          <FiArrowRight size={16} className="cart-auth__opt-arrow" />
        </button>
        <button className="cart-auth__opt cart-auth__opt--guest" onClick={() => onContinue("details", null)}>
          <div className="cart-auth__opt-icon"><FiUser size={26} /></div>
          <div className="cart-auth__opt-text"><strong>Continue as Guest</strong><span>No account required</span></div>
          <FiArrowRight size={16} className="cart-auth__opt-arrow" />
        </button>
      </div>
    </div>
  );

  if (mode === "login") return (
    <div className="cart-auth">
      <button className="cart-back" onClick={() => { setMode("choose"); setError(""); }}><FiChevronLeft size={15} /> Back</button>
      <div className="cart-auth__header"><h2>Sign In</h2><p>Welcome back! Enter your credentials to continue.</p></div>
      <form className="cart-auth__form" onSubmit={handleLogin}>
        {error && <p className="cart-error"><FiAlertCircle size={14} /> {error}</p>}
        <div className="cart-form__group">
          <label>Email address</label>
          <input type="email" required value={loginForm.email} onChange={e => setLoginForm(f => ({ ...f, email: e.target.value }))} placeholder="you@example.com" />
        </div>
        <div className="cart-form__group">
          <label>Password</label>
          <div className="cart-pw-wrap">
            <input type={showPw ? "text" : "password"} required value={loginForm.password} onChange={e => setLoginForm(f => ({ ...f, password: e.target.value }))} placeholder="Your password" />
            <button type="button" className="cart-pw-toggle" onClick={() => setShowPw(v => !v)}>{showPw ? <FiEyeOff size={15} /> : <FiEye size={15} />}</button>
          </div>
        </div>
        <button className="cart-btn cart-btn--primary cart-btn--full" disabled={loading}>{loading ? "Signing in…" : "Sign In & Continue"}</button>
        <p className="cart-auth__switch">Don't have an account? <button type="button" onClick={() => { setMode("register"); setError(""); }}>Create one</button></p>
      </form>
    </div>
  );

  return (
    <div className="cart-auth">
      <button className="cart-back" onClick={() => { setMode("choose"); setError(""); }}><FiChevronLeft size={15} /> Back</button>
      <div className="cart-auth__header"><h2>Create Account</h2><p>Create an account to track orders and save your details.</p></div>
      <form className="cart-auth__form cart-form" onSubmit={handleRegister}>
        {error && <p className="cart-error cart-form__group--full"><FiAlertCircle size={14} /> {error}</p>}
        <div className="cart-form__group"><label>First name *</label><input required value={regForm.first_name} onChange={e => setRegForm(f => ({ ...f, first_name: e.target.value }))} /></div>
        <div className="cart-form__group"><label>Last name *</label><input required value={regForm.last_name} onChange={e => setRegForm(f => ({ ...f, last_name: e.target.value }))} /></div>
        <div className="cart-form__group"><label>Email address *</label><input type="email" required value={regForm.email} onChange={e => setRegForm(f => ({ ...f, email: e.target.value }))} /></div>
        <div className="cart-form__group"><label>Phone number</label><input value={regForm.phone} onChange={e => setRegForm(f => ({ ...f, phone: e.target.value }))} /></div>
        <div className="cart-form__group">
          <label>Password *</label>
          <div className="cart-pw-wrap">
            <input type={showPw ? "text" : "password"} required value={regForm.password} onChange={e => setRegForm(f => ({ ...f, password: e.target.value }))} placeholder="Min. 8 characters" />
            <button type="button" className="cart-pw-toggle" onClick={() => setShowPw(v => !v)}>{showPw ? <FiEyeOff size={15} /> : <FiEye size={15} />}</button>
          </div>
        </div>
        <div className="cart-form__group"><label>Confirm password *</label><input type={showPw ? "text" : "password"} required value={regForm.password2} onChange={e => setRegForm(f => ({ ...f, password2: e.target.value }))} placeholder="Re-enter password" /></div>
        <div className="cart-auth__address-head cart-form__group--full"><FiMapPin size={14} /> Delivery Address <span>(optional — saves time at checkout)</span></div>
        <div className="cart-form__group cart-form__group--full"><label>Street address</label><input value={regForm.address} onChange={e => setRegForm(f => ({ ...f, address: e.target.value }))} /></div>
        <div className="cart-form__group"><label>City</label><input value={regForm.city} onChange={e => setRegForm(f => ({ ...f, city: e.target.value }))} /></div>
        <div className="cart-form__group">
          <label>Province</label>
          <select value={regForm.province} onChange={e => setRegForm(f => ({ ...f, province: e.target.value }))}>
            <option value="">Select…</option>
            {["Gauteng","Western Cape","KwaZulu-Natal","Eastern Cape","Limpopo","Mpumalanga","North West","Free State","Northern Cape"].map(p => <option key={p}>{p}</option>)}
          </select>
        </div>
        <div className="cart-form__group"><label>Postal code</label><input value={regForm.postal_code} onChange={e => setRegForm(f => ({ ...f, postal_code: e.target.value }))} /></div>
        <button className="cart-btn cart-btn--primary cart-btn--full cart-form__group--full" disabled={loading}>{loading ? "Creating account…" : "Create Account & Continue"}</button>
        <p className="cart-auth__switch cart-form__group--full">Already have an account? <button type="button" onClick={() => { setMode("login"); setError(""); }}>Sign in</button></p>
      </form>
    </div>
  );
}

/* ══════════════════════════════════════
   CART PAGE
   ══════════════════════════════════════ */
export default function Cart() {
  const { cart, removeFromCart, setQty, clearCart, cartTotal, cartCount } = useCart();
  const { customer, token } = useCustomer();

  const [step, setStep]                       = useState("cart");
  const [guestOrCustomer, setGuestOrCustomer] = useState(null);
  const [couponCode, setCouponCode]           = useState("");
  const [coupon, setCoupon]                   = useState(null);
  const [couponError, setCouponError]         = useState("");
  const [couponLoading, setCouponLoading]     = useState(false);
  const [bankingDetails, setBankingDetails]   = useState(null);
  const [submitting, setSubmitting]           = useState(false);
  const [orderResult, setOrderResult]         = useState(null);
  const [orderError, setOrderError]           = useState("");

  const [form, setForm] = useState({
    customer_name: "", customer_email: "", customer_phone: "",
    shipping_address: "", payment_method: "EFT", notes: "",
  });

  /* Pre-fill form from customer profile */
  useEffect(() => {
    const c = guestOrCustomer || customer;
    if (c) {
      setForm(f => ({
        ...f,
        customer_name:    f.customer_name    || `${c.first_name} ${c.last_name}`,
        customer_email:   f.customer_email   || c.email || "",
        customer_phone:   f.customer_phone   || c.phone || "",
        shipping_address: f.shipping_address || (c.address
          ? `${c.address}, ${c.city || ""}, ${c.province || ""} ${c.postal_code || ""}`.replace(/,\s*,/g, ",").trim()
          : ""),
      }));
    }
  }, [guestOrCustomer, customer]);

  /* Fetch banking details when reaching checkout */
  useEffect(() => {
    if (step === "details" && bankingDetails === null) {
      fetch(`${process.env.REACT_APP_API_URL}/api/shop/banking-details`)
        .then(r => r.json())
        .then(data => setBankingDetails(Array.isArray(data) ? data : []))
        .catch(() => setBankingDetails([]));
    }
  }, [step]);

  /* ─────────────────────────────────────────────────────────────
     COUPON DISCOUNT — category-aware
     Uses calcCouponDiscount() which only applies the discount
     to items belonging to the coupon's restricted categories.
     If category_ids is empty the coupon covers all items.
     ───────────────────────────────────────────────────────────── */
  const { discountAmount: discountAmt, eligibleSubtotal, eligibleItems } =
    calcCouponDiscount(cart, coupon, cartTotal);

  const finalTotal = Math.max(0, cartTotal - discountAmt);

  /* ── Coupon validation ── */
  const validateCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponLoading(true);
    setCouponError("");
    setCoupon(null);

    try {
      // Pass the unique category IDs from the current cart so the server can
      // also validate that at least one cart item matches the coupon's categories.
      const cartCatIds = [...new Set(cart.map(i => i.category_id).filter(Boolean))];
      const catParam   = cartCatIds.length ? `?categoryIds=${cartCatIds.join(",")}` : "";

      const r = await fetch(`${process.env.REACT_APP_API_URL}/api/coupons/validate/${encodeURIComponent(couponCode.trim())}${catParam}`);
      const d = await r.json();

      if (!r.ok) {
        // 422 = coupon valid but no matching category in cart
        if (r.status === 422) {
          setCouponError(d.message || "This coupon does not apply to any product in your cart.");
        } else {
          setCouponError(d.message || "Invalid or expired coupon.");
        }
        return;
      }

      // Client-side min_order_value guard (server also checks, but show friendly msg here)
      if (d.min_order_value && Number(d.min_order_value) > 0 && cartTotal < Number(d.min_order_value)) {
        setCouponError(`A minimum order of ${fmt(d.min_order_value)} is required for this coupon.`);
        return;
      }

      setCoupon(d);
    } catch {
      setCouponError("Could not validate coupon. Please try again.");
    } finally {
      setCouponLoading(false);
    }
  };

  /* Build a human-readable description of the coupon's scope */
  const couponScopeLabel = () => {
    if (!coupon) return "";
    const catIds = Array.isArray(coupon.category_ids)
      ? coupon.category_ids.map(Number).filter(Boolean)
      : [];
    if (catIds.length === 0) return "Applies to all products";
    const count = eligibleItems.length;
    return `Applies to ${count} eligible item${count !== 1 ? "s" : ""} in your cart`;
  };

  const handleFormChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handlePlaceOrder = async () => {
    setSubmitting(true);
    setOrderError("");
    try {
      const items = cart.map(i => ({
        product_id:   i.id,
        product_name: i.name,
        sku:          i.sku || null,
        quantity:     i.qty,
        unit_price:   Number(i.sale_price || i.price),
        total_price:  Number(i.sale_price || i.price) * i.qty,
      }));

      const resolvedCustomer = guestOrCustomer || customer;
      const body = {
        ...form,
        customer_id:     resolvedCustomer?.id || null,
        items,
        subtotal:        cartTotal,
        discount_amount: discountAmt,
        shipping_cost:   0,
        total:           finalTotal,
        coupon_code:     coupon?.code || null,
      };

      const headers = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const r    = await fetch(`${process.env.REACT_APP_API_URL}/api/shop/orders`, { method: "POST", headers, body: JSON.stringify(body) });
      const data = await r.json();
      if (!r.ok) throw new Error(data.message || "Order failed.");

      setOrderResult(data);
      clearCart();
      setStep("confirm");
    } catch (err) {
      setOrderError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Empty cart ── */
  if (cart.length === 0 && step !== "confirm") return (
    <main className="cart-page">
      <div className="container cart-empty">
        <FiShoppingCart size={60} className="cart-empty__icon" />
        <h2>Your cart is empty</h2>
        <p>Browse our shop and add some parts.</p>
        <Link to="/shop" className="cart-btn cart-btn--primary">Go to Shop <FiArrowRight size={16} /></Link>
      </div>
    </main>
  );

  /* ── Order confirmed ── */
  if (step === "confirm" && orderResult) return (
    <main className="cart-page">
      <div className="container cart-confirm">
        <div className="cart-confirm__icon"><FiCheck size={36} /></div>
        <h2>Order Placed!</h2>
        <p className="cart-confirm__num">Order number: <strong>{orderResult.order_number}</strong></p>
        <p>Thank you for your order. We'll be in touch shortly.</p>
        {form.payment_method === "EFT" && bankingDetails && bankingDetails.length > 0 && (
          <div className="cart-confirm__banking">
            <h4>EFT Payment Details</h4>
            {bankingDetails.map(b => (
              <div key={b.id} className="cart-confirm__bank">
                <p><strong>{b.bank_name}</strong></p>
                <p>Account name: {b.account_name}</p>
                <p>Account number: {b.account_number}</p>
                {b.branch_code && <p>Branch code: {b.branch_code}</p>}
                {b.account_type && <p>Account type: {b.account_type}</p>}
                <p className="cart-confirm__ref">Reference: <strong>{orderResult.order_number}</strong></p>
              </div>
            ))}
            <div className="cart-confirm__pop">
              <p>Send proof of payment to:</p>
              <a href={`https://wa.me/${WHATSAPP_NUMBER}?text=Proof%20of%20payment%20for%20order%20${orderResult.order_number}`} target="_blank" rel="noopener noreferrer" className="cart-pop__link cart-pop__link--whatsapp">
                <WhatsAppIcon /> WhatsApp +27 82 936 2678 <FiExternalLink size={11} />
              </a>
              <span className="cart-confirm__pop-or">or</span>
              <a href={`mailto:${ORDERS_EMAIL}?subject=Proof of Payment - Order ${orderResult.order_number}`} className="cart-pop__link cart-pop__link--email">{ORDERS_EMAIL}</a>
            </div>
          </div>
        )}
        {!customer && (
          <div className="cart-confirm__register-prompt">
            <FiInfo size={15} />
            <span>Want to track this order? <Link to="/register">Create a free account</Link></span>
          </div>
        )}
        <Link to="/shop" className="cart-btn cart-btn--primary" style={{ marginTop: "1.5rem" }}>Continue Shopping</Link>
      </div>
    </main>
  );

  return (
    <main className="cart-page">
      {/* Breadcrumb */}
      <div className="cart-crumb-bar">
        <div className="container cart-crumb">
          <Link to="/">Home</Link><FiArrowRight size={12} />
          <Link to="/shop">Shop</Link><FiArrowRight size={12} />
          <span>Cart</span>
        </div>
      </div>

      {/* Steps */}
      <div className="container cart-steps">
        {["cart","auth","details"].map((s, i) => {
          const labels    = { cart: "Cart", auth: "Account", details: "Checkout" };
          const stepOrder = { cart: 0, auth: 1, details: 2 };
          const current   = stepOrder[step] ?? 0;
          const thisIdx   = stepOrder[s]    ?? 0;
          return (
            <React.Fragment key={s}>
              <div className={`cart-step ${current === thisIdx ? "cart-step--active" : ""} ${current > thisIdx ? "cart-step--done" : ""}`}>
                <div className="cart-step__dot">{current > thisIdx ? <FiCheck size={12} /> : i + 1}</div>
                <span>{labels[s]}</span>
              </div>
              {i < 2 && <div className={`cart-step__line ${current > thisIdx ? "cart-step__line--done" : ""}`} />}
            </React.Fragment>
          );
        })}
      </div>

      <div className="container cart-body">

        {/* ══ STEP: CART ══ */}
        {step === "cart" && (
          <>
            <div className="cart-main">
              <div className="cart-main__head">
                <h2>Your Cart <span>({cartCount} item{cartCount !== 1 ? "s" : ""})</span></h2>
                <button className="cart-clear" onClick={clearCart}><FiTrash2 size={14} /> Clear cart</button>
              </div>

              <div className="cart-items">
                {cart.map(item => {
                  const price    = Number(item.sale_price || item.price);
                  const oldPrice = item.sale_price ? Number(item.price) : null;
                  const imgSrc = shopImg(item.image1);

                  // Highlight items that are eligible for the applied coupon
                  const isCouponRestricted =
                    coupon &&
                    Array.isArray(coupon.category_ids) &&
                    coupon.category_ids.length > 0;
                  const isEligible =
                    !isCouponRestricted ||
                    coupon.category_ids.map(Number).includes(Number(item.category_id));

                  return (
                    <div
                      key={item.id}
                      className={`cart-item${isCouponRestricted && !isEligible ? " cart-item--ineligible" : ""}`}
                    >
                      <div className="cart-item__img">
                        {imgSrc
                          ? <img src={imgSrc} alt={item.name} />
                          : <div className="cart-item__no-img"><FiPackage size={28} /></div>
                        }
                      </div>
                      <div className="cart-item__info">
                        <Link to={`/shop/${item.slug}`} className="cart-item__name">{item.name}</Link>
                        {item.part_number && <p className="cart-item__sku">Part #: {item.part_number}</p>}
                        {item.car_make    && <p className="cart-item__fit">{item.car_make} {item.car_model}</p>}
                        <div className="cart-item__price-row">
                          <span className="cart-item__price">{fmt(price)}</span>
                          {oldPrice && <span className="cart-item__old">{fmt(oldPrice)}</span>}
                        </div>
                        {/* Badge: show when a restricted coupon is active */}
                        {isCouponRestricted && (
                          <span className={`cart-item__coupon-badge ${isEligible ? "cart-item__coupon-badge--yes" : "cart-item__coupon-badge--no"}`}>
                            {isEligible ? <><FiCheck size={11} /> Coupon applied</> : "Coupon not applicable"}
                          </span>
                        )}
                      </div>
                      <div className="cart-item__actions">
                        <div className="cart-item__qty">
                          <button onClick={() => setQty(item.id, item.qty - 1)} disabled={item.qty <= 1}><FiMinus size={13} /></button>
                          <span>{item.qty}</span>
                          <button onClick={() => setQty(item.id, item.qty + 1)} disabled={item.qty >= item.quantity}><FiPlus size={13} /></button>
                        </div>
                        <p className="cart-item__line">{fmt(price * item.qty)}</p>
                        <button className="cart-item__remove" onClick={() => removeFromCart(item.id)}><FiTrash2 size={15} /></button>
                      </div>
                    </div>
                  );
                })}
              </div>

              <Link to="/shop" className="cart-back"><FiChevronLeft size={15} /> Continue shopping</Link>
            </div>

            {/* ── Order Summary sidebar ── */}
            <div className="cart-summary">
              <h3>Order Summary</h3>

              {/* Coupon input */}
              <div className="cart-coupon">
                <label><FiTag size={13} /> Coupon code</label>
                <div className="cart-coupon__row">
                  <input
                    value={couponCode}
                    onChange={e => { setCouponCode(e.target.value); setCouponError(""); }}
                    placeholder="Enter code"
                    onKeyDown={e => e.key === "Enter" && validateCoupon()}
                  />
                  <button onClick={validateCoupon} disabled={couponLoading}>
                    {couponLoading ? "…" : "Apply"}
                  </button>
                </div>
                {couponError && (
                  <p className="cart-coupon__err"><FiAlertCircle size={13} /> {couponError}</p>
                )}
                {coupon && (
                  <>
                    <p className="cart-coupon__ok">
                      <FiCheck size={13} />
                      {coupon.description ||
                        (coupon.discount_type === "percent"
                          ? `${coupon.discount_value}% off`
                          : `${fmt(coupon.discount_value)} off`)}
                      {" "}
                      <button
                        className="cart-coupon__remove"
                        onClick={() => { setCoupon(null); setCouponCode(""); }}
                        title="Remove coupon"
                      >
                        ✕
                      </button>
                    </p>
                    {/* Scope hint — only shown when restricted to categories */}
                    {Array.isArray(coupon.category_ids) && coupon.category_ids.length > 0 && (
                      <p className="cart-coupon__scope">
                        <FiInfo size={11} /> {couponScopeLabel()}
                        {eligibleSubtotal > 0 && (
                          <> — eligible subtotal {fmt(eligibleSubtotal)}</>
                        )}
                      </p>
                    )}
                  </>
                )}
              </div>

              {/* Totals */}
              <div className="cart-summary__lines">
                <div className="cart-summary__line">
                  <span>Subtotal</span>
                  <span>{fmt(cartTotal)}</span>
                </div>
                {discountAmt > 0 && (
                  <div className="cart-summary__line cart-summary__line--discount">
                    <span>
                      Discount
                      {/* Show which subtotal the discount is based on when restricted */}
                      {coupon && Array.isArray(coupon.category_ids) && coupon.category_ids.length > 0
                        ? ` (on ${fmt(eligibleSubtotal)})`
                        : ""}
                    </span>
                    <span>− {fmt(discountAmt)}</span>
                  </div>
                )}
                <div className="cart-summary__line">
                  <span>Shipping</span>
                  <span className="cart-summary__tbd">TBD</span>
                </div>
                <div className="cart-summary__line cart-summary__line--total">
                  <span>Total</span>
                  <span>{fmt(finalTotal)}</span>
                </div>
              </div>

              <button
                className="cart-btn cart-btn--primary cart-btn--full"
                onClick={() => {
                  if (customer) { setGuestOrCustomer(customer); setStep("details"); }
                  else          { setStep("auth"); }
                }}
              >
                Proceed to Checkout <FiArrowRight size={16} />
              </button>
            </div>
          </>
        )}

        {/* ══ STEP: AUTH ══ */}
        {step === "auth" && (
          <div className="cart-auth-wrap">
            <AuthGate onContinue={(nextStep, resolved) => {
              setGuestOrCustomer(resolved);
              setStep(nextStep);
            }} />
          </div>
        )}

        {/* ══ STEP: CHECKOUT DETAILS ══ */}
        {step === "details" && (
          <>
            <div className="cart-checkout">
              <button
                className="cart-back"
                onClick={() => customer ? setStep("cart") : setStep("auth")}
              >
                <FiChevronLeft size={15} /> Back
              </button>
              <h2>Delivery &amp; Contact Details</h2>

              {(guestOrCustomer || customer) && (
                <div className="cart-checkout__logged-in">
                  <FiUser size={14} />
                  Checking out as <strong>{(guestOrCustomer || customer).email}</strong>
                </div>
              )}

              <div className="cart-form">
                <div className="cart-form__group">
                  <label>Full name *</label>
                  <input name="customer_name" value={form.customer_name} onChange={handleFormChange} required />
                </div>
                <div className="cart-form__group">
                  <label>Email *</label>
                  <input name="customer_email" type="email" value={form.customer_email} onChange={handleFormChange} required />
                </div>
                <div className="cart-form__group">
                  <label>Phone</label>
                  <input name="customer_phone" value={form.customer_phone} onChange={handleFormChange} />
                </div>
                <div className="cart-form__group">
                  <label>Payment method</label>
                  <select name="payment_method" value={form.payment_method} onChange={handleFormChange}>
                    <option value="EFT">EFT (Bank Transfer)</option>
                    <option value="Cash">Cash on Collection</option>
                  </select>
                </div>
                <div className="cart-form__group cart-form__group--full">
                  <label>Shipping address</label>
                  <AddressAutocomplete name="shipping_address" value={form.shipping_address} onChange={handleFormChange} />
                </div>
                <div className="cart-form__group cart-form__group--full">
                  <label>Order notes (optional)</label>
                  <textarea name="notes" value={form.notes} onChange={handleFormChange} rows={2} placeholder="Any special instructions…" />
                </div>
              </div>

              {/* Summary recap */}
              <div className="cart-checkout__summary">
                <div className="cart-summary__line">
                  <span>Subtotal ({cartCount} item{cartCount !== 1 ? "s" : ""})</span>
                  <span>{fmt(cartTotal)}</span>
                </div>
                {discountAmt > 0 && (
                  <div className="cart-summary__line cart-summary__line--discount">
                    <span>
                      Discount{coupon ? ` (${coupon.code})` : ""}
                      {coupon && Array.isArray(coupon.category_ids) && coupon.category_ids.length > 0
                        ? ` on ${fmt(eligibleSubtotal)}`
                        : ""}
                    </span>
                    <span>− {fmt(discountAmt)}</span>
                  </div>
                )}
                <div className="cart-summary__line cart-summary__line--total">
                  <span>Total</span>
                  <span>{fmt(finalTotal)}</span>
                </div>
              </div>

              {orderError && (
                <p className="cart-error"><FiAlertCircle size={14} /> {orderError}</p>
              )}

              <button
                className="cart-btn cart-btn--primary cart-btn--full"
                onClick={handlePlaceOrder}
                disabled={submitting || !form.customer_name || !form.customer_email}
              >
                {submitting ? "Placing order…" : `Place Order · ${fmt(finalTotal)}`}
              </button>
            </div>

            {/* Right panel: banking + delivery + POP */}
            <BankingPanel paymentMethod={form.payment_method} bankingDetails={bankingDetails} />
          </>
        )}

      </div>
    </main>
  );
}